import React from "react";
import { Box, Typography, Card, CardContent, Stack } from "@mui/material";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import PropertyFileUpload from "../../PropertyFileUpload";
import PropertySourcePicker from "./PropertySourcePicker";
import { StepProps } from "../types";

const TEAL = "#00d4ad";

const cardSx = {
  border: "1px solid #e6e8eb",
  boxShadow: "none",
  borderRadius: 2,
};

const SectionHeader: React.FC<{ icon: React.ReactNode; title: string; subtitle: string }> = ({
  icon,
  title,
  subtitle,
}) => (
  <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 2 }}>
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: 1.5,
        backgroundColor: "rgba(0,212,173,0.12)",
        color: TEAL,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#1a1a1a", lineHeight: 1.3 }}>
        {title}
      </Typography>
      <Typography variant="caption" sx={{ color: "#6b7280" }}>
        {subtitle}
      </Typography>
    </Box>
  </Box>
);

/** Optional property (parcel) boundaries for the new project: from any
 *  number of the organisation's property-boundary data sources, from a
 *  file, or both. Sources are pulled over the project area during
 *  creation; the file is uploaded with the project. */
const PropertyBoundariesStep: React.FC<StepProps> = ({ data, onUpdateData }) => {
  const handlePropertyFilesChange: Parameters<typeof PropertyFileUpload>[0]["onFileSelected"] = (file) => {
    onUpdateData({ propertyFiles: file });
  };

  const sourceCount = data.selectedPropertySources.length;

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", backgroundColor: "#fafbfc" }}>
      {/* Header */}
      <Box sx={{ px: 4, py: 3, backgroundColor: "#fff", borderBottom: "1px solid #e6e8eb" }}>
        <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 600, color: "#1a1a1a" }}>
          Property Boundaries
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b7280" }}>
          Optional. Load property boundaries from your data sources, a file, or both.
        </Typography>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflow: "auto", px: 4, py: 4 }}>
        <Stack spacing={3} sx={{ maxWidth: 720, mx: "auto" }}>
          <Card sx={cardSx}>
            <CardContent sx={{ p: 3 }}>
              <SectionHeader
                icon={<HomeWorkOutlinedIcon fontSize="small" />}
                title="From data sources"
                subtitle={
                  sourceCount > 0
                    ? `${sourceCount} source${sourceCount === 1 ? "" : "s"} selected — parcels within the project area are loaded when the project is created`
                    : "Select one or more property boundary sources; parcels within the project area are loaded when the project is created"
                }
              />
              <PropertySourcePicker
                selected={data.selectedPropertySources}
                onChange={(selected) => onUpdateData({ selectedPropertySources: selected })}
              />
            </CardContent>
          </Card>

          <Card sx={cardSx}>
            <CardContent sx={{ p: 3 }}>
              <SectionHeader
                icon={<UploadFileOutlinedIcon fontSize="small" />}
                title="From a file"
                subtitle="A zipped shapefile, KML or GeoJSON of property polygons, added alongside any sources above"
              />
              <PropertyFileUpload onFileSelected={handlePropertyFilesChange} />
            </CardContent>
          </Card>
        </Stack>
      </Box>

      {/* Global navigation bar handles Previous/Next */}
    </Box>
  );
};

export default PropertyBoundariesStep;
